import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

import { cn } from "../ui/utils";

/**
 * Props for DataTableLoadingOverlay.
 *
 * A translucent overlay intended to sit inside a relatively-positioned table
 * wrapper while keeping previously-loaded rows visible (e.g. when switching
 * pages). Returns `null` when `loading` is false.
 */
export type DataTableLoadingOverlayProps = {
  /** Whether to render the overlay. */
  loading: boolean;
  /** Optional loading label. Defaults to `common.loading`. */
  label?: string;
  /** Extra className for the overlay wrapper. */
  className?: string;
};

/**
 * Overlay-style loading indicator for data tables that already have data.
 *
 * Pair with `DataTableStateRow` for the initial-load / empty scenarios. See
 * `docs/数据表格组件指南.md`.
 */
export function DataTableLoadingOverlay({
  loading,
  label,
  className,
}: DataTableLoadingOverlayProps) {
  const { t } = useTranslation();

  if (!loading) return null;

  return (
    <div
      className={cn(
        "absolute inset-0 z-10 flex items-center justify-center bg-slate-50/60 dark:bg-[#1a1d1f]/60",
        className,
      )}
    >
      <span className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
        <Loader2 className="w-4 h-4 animate-spin" />
        {label ?? t("common.loading")}
      </span>
    </div>
  );
}
